import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Divider,
} from "@mui/material";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import RefreshIcon from "@mui/icons-material/Refresh";
import LocationOnIcon from "@mui/icons-material/LocationOn";

// Fix for default Marker icon issue with Webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

// Custom icon for devices
const deviceIcon = new L.Icon({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  iconRetinaUrl:
    "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

// Component to animate map bounds to markers with zooming effect
const FitBoundsToMarkers = ({ devices }) => {
  const map = useMap();

  useEffect(() => {
    if (devices.length === 0) return;

    const validDevices = devices.filter(
      (device) => device.location?.latitude && device.location?.longitude
    );

    if (validDevices.length > 0) {
      const bounds = L.latLngBounds(
        validDevices.map((device) => [
          device.location.latitude,
          device.location.longitude,
        ])
      );
      // Use flyToBounds for a smooth zooming and panning animation
      map.flyToBounds(bounds, {
        padding: [50, 50],
        maxZoom: 13,
        duration: 1.5, // Animation duration in seconds
        easeLinearity: 0.25, // Smoothness of the animation
      });
    } else {
      // If no valid devices, animate to a global view
      map.flyTo([0, 0], 2, {
        duration: 1.5,
        easeLinearity: 0.25,
      });
    }
  }, [devices, map]);

  return null;
};

const DeviceLocationMapCard = ({ siteId }) => {
  const { accessToken } = useAuth();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  const baseURL = process.env.REACT_APP_BASEURL;

  useEffect(() => {
    const controller = new AbortController();

    const fetchDevices = async () => {
      if (!accessToken) return;

      setLoading((prevLoading) => (refreshing ? prevLoading : true));
      setRefreshing(true);
      setError(null);

      try {
        const deviceUrl =
          siteId === "all"
            ? `${baseURL}/devices`
            : `${baseURL}/devices/?site=${siteId}`
        const res = await axios.get(deviceUrl, {
          headers: { Authorization: `Bearer ${accessToken}` },
          signal: controller.signal,
          timeout: 20000, // 20 second timeout
        });

        // Make sure data is properly formatted and has valid locations
        const validDevices = (res.data || []).map((device) => ({
          ...device,
          // Ensure consistent location structure
          location: {
            latitude: device.location?.latitude || device.latitude,
            longitude: device.location?.longitude || device.longitude,
          },
          status: device.device_status,
          // Format last_seen if it exists
          last_seen: device.last_seen
            ? new Date(device.last_seen).toLocaleString()
            : "Unknown",
        }));

        setDevices(validDevices);
      } catch (error) {
        // Ignore aborted requests
        if (axios.isCancel(error)) return;

        console.error(
          "Device location fetch error:",
          error.response ? error.response.data : error.message
        );
        setError("Failed to load device locations. Please try again later.");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    };

    fetchDevices();

    // Cleanup function to abort fetch if component unmounts
    return () => controller.abort();
  }, [accessToken, retryCount, siteId, baseURL]);

  const handleRetry = () => {
    setRetryCount((prev) => prev + 1);
  };

  const handleRefresh = () => {
    setRetryCount((prev) => prev + 1);
  };

  const validDevices = useMemo(
    () =>
      devices.filter(
        (device) => device.location?.latitude && device.location?.longitude
      ),
    [devices]
  );

  const statusColor = (status) => {
    if (!status) return "#9e9e9e";

    switch (status.toLowerCase()) {
      case "ok":
        return "#4caf50";
      case "offline":
      case "waiting":
        return "#f44336";
      case "idle":
        return "#ff9800";
      default:
        return "#9e9e9e";
    }
  };

  const formatCoordinates = (lat, lng) => {
    if (!lat || !lng) return "Unknown";
    return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
  };

  return (
    <Card
      sx={{
        width: "100%",
        height: "auto",
        borderRadius: 15,
        border: "2px solid black",
        overflow: "hidden",
        backgroundColor: "#fff",
        color: "#000",
        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
        transition: "box-shadow 0.3s ease",
        "&:hover": {
          boxShadow: "0 6px 16px rgba(0,0,0,0.15)",
        },
      }}
    >
      <CardContent sx={{ p: 0 }}>
        <Box
          sx={{
            p: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            borderBottom: "1px solid #f0f0f0",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <LocationOnIcon sx={{ mr: 1, color: "#00796B" }} />
            <Typography variant="h4" fontWeight="bold" color="#00796B">
              Device Locations
            </Typography>
          </Box>
          <IconButton
            onClick={handleRefresh}
            disabled={loading || refreshing}
            size="small"
            sx={{
              color: "#00796B",
              animation: refreshing ? "spin 1s linear infinite" : "none",
              "@keyframes spin": {
                "0%": { transform: "rotate(0deg)" },
                "100%": { transform: "rotate(360deg)" },
              },
            }}
            aria-label="Refresh device locations"
          >
            <RefreshIcon />
          </IconButton>
        </Box>

        {error && (
          <Alert
            severity="error"
            sx={{ mx: 2, mt: 2, mb: 0 }}
            action={
              <IconButton
                color="inherit"
                size="small"
                onClick={handleRetry}
                aria-label="Retry loading device locations"
              >
                <RefreshIcon />
              </IconButton>
            }
          >
            {error}
          </Alert>
        )}

        <Box
          sx={{
            height: 290,
            width: "100%",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {loading && !refreshing ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                backgroundColor: "#f9f9f9",
              }}
            >
              <CircularProgress
                size={40}
                thickness={4}
                sx={{ color: "#00796B" }}
              />
              <Typography sx={{ mt: 2, color: "#757575" }}>
                Loading device locations...
              </Typography>
            </Box>
          ) : (
            <MapContainer
              center={[0, 0]} // Initial center to initialize the map
              zoom={2} // Initial zoom level for a global view
              style={{ height: "100%", width: "100%" }}
              scrollWheelZoom={true}
              zoomControl={true}
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              />

              {validDevices.map((device) => (
                <Marker
                  key={device.id || device._id}
                  position={[
                    device.location.latitude,
                    device.location.longitude,
                  ]}
                  icon={deviceIcon}
                >
                  <Popup className="device-popup">
                    <Box sx={{ minWidth: 100, maxWidth: 400 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: "bold" }}
                      >
                        {device.name || "Unnamed Device"}
                      </Typography>
                      <Divider />
                      <Box sx={{ display: "flex" }}>
                        <Typography variant="body3" color="text.primary">
                          ID: {device.id}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex" }}>
                        <Typography variant="body3" color="text.primary">
                          Status:
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Box
                            sx={{
                              width: 10,
                              height: 10,
                              borderRadius: "50%",
                              backgroundColor: statusColor(device.status),
                              mr: 0.5,
                            }}
                          />
                          <Typography variant="body3">
                            {device.status || "Unknown"}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: "flex", mb: 1 }}>
                        <Typography variant="body3" color="text.primary">
                          Last Seen: {device.last_seen}
                        </Typography>
                      </Box>

                      <Box sx={{ mb: 1 }}>
                        <Box sx={{ display: "flex" }}>
                          <Typography variant="body3" color="text.primary">
                          Coordinates: {formatCoordinates(device.location.latitude, device.location.longitude)}
                          </Typography>
                        </Box>
                      </Box>

                      <Box sx={{ mb: 1 }}>
                        <Box>
                          <Typography variant="body3" color="text.primary">
                            Moisture (%):{" "}
                            {device.last_measurement?.moisture * 100}
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex" }}>
                          <Typography variant="body3" color="text.primary">
                            Temperature (°C):{" "}
                            {device.last_measurement?.temperature}
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex" }}>
                          <Typography variant="body3" color="text.primary">
                            Salinity (dS/m): {device.last_measurement?.salinity}
                          </Typography>
                        </Box>
                      </Box>

                      <Box sx={{ display: "flex", mb: 1 }}>
                        <Typography variant="body3" color="text.primary">
                          Battery Voltage (V): {device.voltage_battery}
                        </Typography>
                      </Box>
                    </Box>
                  </Popup>
                </Marker>
              ))}

              <FitBoundsToMarkers devices={devices} />
            </MapContainer>
          )}

          {refreshing && (
            <Box
              sx={{
                position: "absolute",
                top: 10,
                right: 10,
                backgroundColor: "rgba(255,255,255,0.8)",
                borderRadius: 1,
                px: 1,
                py: 0.5,
                display: "flex",
                alignItems: "center",
              }}
            >
              <CircularProgress size={16} sx={{ mr: 1, color: "#00796B" }} />
              <Typography variant="caption">Refreshing...</Typography>
            </Box>
          )}
        </Box>

        <Box sx={{ pt: 1 }}>
          {!loading && validDevices.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                py: 1,
              }}
            >
              <LocationOnIcon sx={{ color: "#9e9e9e", fontSize: 32, mb: 1 }} />
              <Typography variant="body2" color="text.secondary" align="center">
                No devices with location data available.
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Typography variant="body3" color="text.primary">
                {validDevices.length} device
                {validDevices.length !== 1 ? "s" : ""} displayed
              </Typography>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default DeviceLocationMapCard;
